import { t } from '../ui/lang';
const EMPTY = { farms: [], claims: [], limit: 0 };
/** Last snapshot fetched from the server. Read by the dashboard on render. */
let snapshot = EMPTY;
/** Udids currently claimed by this operator — the pin buttons' synchronous source. */
export const workspaceUdids = new Set();
/** Farm id a claimed udid belongs to, so the pin can address the (farm, udid) pair. */
const claimFarmByUdid = new Map();
export function getWorkspaceSnapshot() {
    return snapshot;
}
export function inWorkspace(udid) {
    return workspaceUdids.has(udid);
}
export function notifyWorkspaceChange(udid, claimed) {
    window.dispatchEvent(new CustomEvent('workspace-change', {
        detail: { udid, inWorkspace: claimed },
    }));
}
export function workspaceSize() {
    return workspaceUdids.size;
}
function adopt(next) {
    snapshot = next;
    workspaceUdids.clear();
    claimFarmByUdid.clear();
    for (const c of next.claims) {
        workspaceUdids.add(c.udid);
        claimFarmByUdid.set(c.udid, c.farmId);
    }
}
/**
 * Fetch the workspace. Called at bootstrap (so pins render correctly on the very first
 * farm grid paint) and whenever the dashboard renders or refreshes. A failure leaves
 * the previous snapshot in place — a network blip must not silently empty the pins.
 */
let inFlight = null;
export async function loadWorkspace() {
    // Bootstrap and the dashboard's own render both want the workspace on a cold start;
    // share the in-flight request rather than issuing the same call twice.
    if (inFlight)
        return inFlight;
    inFlight = fetchWorkspace().finally(() => { inFlight = null; });
    return inFlight;
}
async function fetchWorkspace() {
    try {
        const res = await fetch('/api/me/workspace');
        if (!res.ok)
            return snapshot;
        const body = await res.json();
        adopt({
            farms: body.farms ?? [],
            claims: body.claims ?? [],
            limit: body.limit ?? 0,
        });
    }
    catch { /* keep the previous snapshot */ }
    return snapshot;
}
/**
 * Claim a device. Idempotent server-side (ON CONFLICT DO NOTHING), so a double click or
 * a second browser tab is a no-op rather than an error. Resolves to null on success or
 * to the server's message on refusal (e.g. the workspace limit).
 */
export async function addToWorkspace(farmId, udid) {
    try {
        const res = await fetch(`/api/farms/${farmId}/devices/${encodeURIComponent(udid)}/workspace`, { method: 'PUT' });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            return data.error || t('workspace.err_add');
        }
    }
    catch {
        return t('workspace.err_network');
    }
    workspaceUdids.add(udid);
    claimFarmByUdid.set(udid, farmId);
    return null;
}
/** Release a claim. Idempotent — removing a device that isn't claimed still succeeds. */
export async function removeFromWorkspace(farmId, udid) {
    try {
        const res = await fetch(`/api/farms/${farmId}/devices/${encodeURIComponent(udid)}/workspace`, { method: 'DELETE' });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            return data.error || t('workspace.err_remove');
        }
    }
    catch {
        return t('workspace.err_network');
    }
    workspaceUdids.delete(udid);
    claimFarmByUdid.delete(udid);
    return null;
}
/**
 * Release every claim in one call. Resolves to the udids that were released, or to an
 * error message. The local set is only cleared once the server confirms — a failed clear
 * must not leave the UI pretending the board is empty.
 */
export async function clearWorkspace() {
    try {
        const res = await fetch('/api/me/workspace', { method: 'DELETE' });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            return { error: data.error || t('workspace.err_clear') };
        }
        const body = await res.json();
        const removed = body.removed ?? [];
        workspaceUdids.clear();
        claimFarmByUdid.clear();
        snapshot.claims = [];
        return { removed };
    }
    catch {
        return { error: t('workspace.err_network') };
    }
}
/**
 * The farm a device should be claimed under. Prefers the id the caller already knows
 * (a card always knows its farm); falls back to the recorded claim so a Remove issued
 * from the dashboard still addresses the right pair.
 */
export function claimFarmId(udid, known) {
    return known ?? claimFarmByUdid.get(udid) ?? null;
}
